import CurrentUser from 'auth/CurrentUser';
import Icon from 'components/IconDeprecated';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import React, { useState, useEffect } from 'react';
import DriftBot, { getUserAttributes } from 'components/Drift/DriftBot';
import { openWidget } from 'components/Zendesk/Zendesk';
import ZendeskBot from 'components/Zendesk/ZendeskBot';
import LoadingIndicator from 'components/LoadingIndicator';
import User from 'src/entities/User';

export type DriftApi = {
  setUserAttributes: ({ liveChatEnabled }: { liveChatEnabled: boolean }) => void;
  sidebar: {
    open: () => void;
  };
  widget: {
    show: () => void;
    hide: () => void;
  };
};

export type DriftBotType = {
  reset: () => void;
  on: (
    driftEvent: string,
    eventFunction: (api: DriftApi, eventData: DriftEventData) => void
  ) => void;
  api: DriftApi;
};

export type DriftEventData = {
  isOnline: boolean;
};

declare global {
  interface Window {
    drift: DriftBotType;
  }
}

const ZendeskMessenger = (liveAgent: boolean, user: User) => {
  const featureFlag = user?.hasFeature('2021.jun.saas.live_support_chat');
  const allowZendeskChat = liveAgent && featureFlag;

  return (
    <>
      <ZendeskBot />
      {allowZendeskChat ? (
        <MediaObject center gutter="small">
          <Icon name="replies" size="large" />
          <Text>
            <Link onClick={openWidget} eventElementName="SupportDialog-actions-link">
              <MediaObject center gutter="xxsmall">
                <Text weight="semibold">Chat with a live agent</Text>
                <Text size="xxxsmall">
                  <Icon name="chevron-right" size="inherit" />
                </Text>
              </MediaObject>
            </Link>
            <Text size="xxsmall" color="graphite">
              Chat with a Remind team member Monday through Friday, 6am-5pm PT.
            </Text>
          </Text>
        </MediaObject>
      ) : (
        <></>
      )}
    </>
  );
};

// TODO: delete this code when drift has been successfully migrated off of
const DriftChat = (liveAgent: boolean, user: User) => {
  const [loading, setLoading] = useState(true);
  const [isChatOnline, setIsOnline] = useState(false);
  const [driftBotReady, setDriftBotReady] = useState(false);
  const featureFlag = user?.hasFeature('2021.jun.saas.live_support_chat');
  const driftBotLive = () => liveAgent && isChatOnline && featureFlag && driftBotReady;

  useEffect(() => {
    if (window.drift) {
      window.drift.on('ready', handleDriftReady);
    }

    setTimeout(() => {
      setLoading(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!loading && window.drift) {
      window.drift.api.setUserAttributes({
        liveChatEnabled: driftBotLive(),
      });
    }
  }, [loading, liveAgent, isChatOnline, featureFlag, driftBotReady]);

  const attributes = {
    ...getUserAttributes(),
    lastName: user.getLastName(),
  };

  const handleDriftReady = (api: DriftApi, eventData: DriftEventData) => {
    setDriftBotReady(true);
    setIsOnline(eventData.isOnline);

    api.setUserAttributes({
      liveChatEnabled: driftBotLive(),
    });
  };

  const onClickGetHelp = () => {
    window.drift.api.widget.show();
    window.drift.api.sidebar.open();
  };

  return (
    <>
      {loading ? (
        <LoadingIndicator align="center" />
      ) : driftBotLive() ? (
        <MediaObject center gutter="small">
          <Icon name="replies" size="large" />
          <Text>
            <Link onClick={onClickGetHelp} eventElementName="SupportDialog-actions-link">
              <MediaObject center gutter="xxsmall">
                <Text weight="semibold">Chat with a live agent</Text>
                <Text size="xxxsmall">
                  <Icon name="chevron-right" size="inherit" />
                </Text>
              </MediaObject>
            </Link>
            <Text size="xxsmall" color="graphite">
              Chat with a Remind team member Monday through Friday, 6am-5pm PT.
            </Text>
          </Text>
        </MediaObject>
      ) : (
        <>
          <MediaObject center gutter="small">
            <Icon name="replies" size="large" />
            <Text>
              <div>
                <Link
                  onClick={onClickGetHelp}
                  disabled={!driftBotReady}
                  tooltip={!driftBotReady && 'Chat bot could not be loaded'}
                  tooltipPlacement="top"
                  eventElementName="SupportDialog-actions-link"
                >
                  <MediaObject center gutter="xxsmall">
                    <Text weight="semibold">Chat now</Text>
                    <Text size="xxxsmall">
                      <Icon name="chevron-right" size="inherit" />
                    </Text>
                  </MediaObject>
                </Link>
              </div>
              <Text size="xxsmall" color="graphite">
                Get immediate answers from our support bot, Richard
              </Text>
            </Text>
          </MediaObject>
        </>
      )}
      <DriftBot attributes={attributes} />
    </>
  );
};

const SupportChat = ({ liveAgent = false }: { liveAgent: boolean }) => {
  const user = CurrentUser.getUser();
  const useZendesk = user?.hasFeature('zendeskMessenger.web.jan-2025');

  if (useZendesk) {
    return ZendeskMessenger(liveAgent, user);
  } else {
    return DriftChat(liveAgent, user);
  }
};

export default SupportChat;
